// src/pages/AdminProductManager.js

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { escapeCSV, downloadCSV } from '../utils/csvExport';
import ConfirmModal from '../components/ConfirmModal';
import './Catalog.css';
import './AdminProductManager.css';
import './AdminProductManager_Mobile.css';
import './StockModals.css';
import AdminHeader from '../components/AdminHeader';

function AdminProductManager() {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('adminCategories') || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [stockStatus, setStockStatus] = useState('all'); // all, in-stock, low-stock, out-of-stock
  const [featuredFilter, setFeaturedFilter] = useState('all'); // all, featured, non-featured
  const [sortBy, setSortBy] = useState('name-asc'); // name-asc, name-desc, price-asc, price-desc, stock-asc, stock-desc
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [addStockProducts, setAddStockProducts] = useState([]);
  const [addStockSelectedProduct, setAddStockSelectedProduct] = useState(null);
  const [addStockQuantity, setAddStockQuantity] = useState(1);
  const [addStockSearch, setAddStockSearch] = useState('');
  const [addStockLoading, setAddStockLoading] = useState(false);
  const [showRemoveStockModal, setShowRemoveStockModal] = useState(false);
  const [removeStockProducts, setRemoveStockProducts] = useState([]);
  const [removeStockSelectedProduct, setRemoveStockSelectedProduct] = useState(null);
  const [removeStockQuantity, setRemoveStockQuantity] = useState(1);
  const [removeStockSearch, setRemoveStockSearch] = useState('');
  const [removeStockLoading, setRemoveStockLoading] = useState(false);
  const [soldInStore, setSoldInStore] = useState(true); // Toggle: true = sold in-store, false = just remove
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [reorderMode, setReorderMode] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [showDeleteProductModal, setShowDeleteProductModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Check if user is super admin from API
  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsSuperAdmin(false);
          return;
        }
        
        const res = await fetch(`${process.env.REACT_APP_API}/api/user/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const userData = await res.json();
          console.log('User data from API:', userData);
          console.log('User role:', userData.role);
          console.log('Is superadmin?', userData.role === 'superadmin');
          setIsSuperAdmin(userData.role === 'superadmin');
        } else {
          setIsSuperAdmin(false);
        }
      } catch (error) {
        console.error('Error checking super admin status:', error);
        setIsSuperAdmin(false);
      }
    };
    
    checkSuperAdmin();
  }, []);

  // Track window resize for responsive pagination
  useEffect(() => {
    const handleResize = () => {
      const wasMobile = isMobile;
      const nowMobile = window.innerWidth <= 768;
      setIsMobile(nowMobile);
      // Reset to page 1 when switching between mobile/desktop to avoid showing wrong item count
      if (wasMobile !== nowMobile) {
        setPage(1);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    careGuide: '',
    category: '',
    tags: [],
    stock: '',
    openingStock: '',
    price: '',
    discount: '',
    discountType: 'percentage', // Only percentage allowed now
    image: '',
    images: []  // Array for multiple images
  });

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);



  // Toggle featured flag for a product (admin)
  const handleToggleFeatured = async (productId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/${productId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ featured: !currentValue })
      });
      if (!res.ok) throw new Error('Failed to update featured');
      setProducts(prev => prev.map(p => p._id === productId ? { ...p, featured: !currentValue } : p));
    } catch (err) {
      console.error('Failed to toggle featured:', err);
      alert('Could not update featured status.');
    }
  };

  // Toggle lowStock flag (admin override). Note: low stock is also computed client-side when stock <= 10
  const handleToggleLowStock = async (productId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/${productId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ lowStock: !currentValue })
      });
      if (!res.ok) throw new Error('Failed to update lowStock');
      setProducts(prev => prev.map(p => p._id === productId ? { ...p, lowStock: !currentValue } : p));
    } catch (err) {
      console.error('Failed to toggle lowStock:', err);
      alert('Could not update low-stock status.');
    }
  };

  // Toggle onSale flag (admin)
  

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'All') params.append('category', selectedCategory);
      if (minPrice) params.append('min', minPrice);
      if (maxPrice) params.append('max', maxPrice);
      if (stockStatus !== 'all') params.append('stockStatus', stockStatus);
      if (featuredFilter !== 'all') params.append('featured', featuredFilter === 'featured' ? 'true' : 'false');
      if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());
      
      // Use responsive page size: 8 mobile (2x4), 12 desktop (4x3)
      const itemsPerPageValue = isMobile ? 8 : 12;
      params.append('limit', itemsPerPageValue);
      params.append('page', page);
      
      // Pass sort to backend for server-side sorting
      if (sortBy) {
        params.append('sort', sortBy);
      }
      
      const res = await fetch(`${process.env.REACT_APP_API}/api/products?${params.toString()}`);
      const data = await res.json();
      
      setProducts(data.products || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setLoading(false);
    }
  }, [page, selectedCategory, minPrice, maxPrice, stockStatus, featuredFilter, sortBy, debouncedSearchQuery, isMobile]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // On mount, try to load global category order from backend (overrides localStorage/defaults)
  useEffect(() => {
    const loadGlobalOrder = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/products/categories/order`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.order) && data.order.length) {
          setCategories(data.order);
        }
      } catch (e) {
        // Ignore network errors; fallback remains
        console.warn('Failed to load global category order');
      }
    };
    loadGlobalOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy load products for Add Stock modal based on search
  useEffect(() => {
    if (!showAddStockModal) return;
    
    const fetchAddStockProducts = async () => {
      setAddStockLoading(true);
      try {
        const params = new URLSearchParams();
        if (addStockSearch.trim()) {
          params.append('search', addStockSearch.trim());
        }
        params.append('limit', 50); // Limit to 50 products for performance
        
        const res = await fetch(`${process.env.REACT_APP_API}/api/products?${params.toString()}`);
        const data = await res.json();
        setAddStockProducts(data.products || []);
      } catch (err) {
        console.error('Failed to load products for add-stock modal', err);
        setAddStockProducts([]);
      } finally {
        setAddStockLoading(false);
      }
    };

    const timer = setTimeout(fetchAddStockProducts, 300); // Debounce search
    return () => clearTimeout(timer);
  }, [showAddStockModal, addStockSearch]);

  // Lazy load products for Remove Stock modal based on search
  useEffect(() => {
    if (!showRemoveStockModal) return;
    
    const fetchRemoveStockProducts = async () => {
      setRemoveStockLoading(true);
      try {
        const params = new URLSearchParams();
        if (removeStockSearch.trim()) {
          params.append('search', removeStockSearch.trim());
        }
        params.append('limit', 50); // Limit to 50 products for performance
        
        const res = await fetch(`${process.env.REACT_APP_API}/api/products?${params.toString()}`);
        const data = await res.json();
        setRemoveStockProducts(data.products || []);
      } catch (err) {
        console.error('Failed to load products for remove-stock modal', err);
        setRemoveStockProducts([]);
      } finally {
        setRemoveStockLoading(false);
      }
    };

    const timer = setTimeout(fetchRemoveStockProducts, 300); // Debounce search
    return () => clearTimeout(timer);
  }, [showRemoveStockModal, removeStockSearch]);

  useEffect(() => {
    localStorage.setItem('adminCategories', JSON.stringify(categories));
  }, [categories]);

  // Persist category order globally for users (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        await fetch(`${process.env.REACT_APP_API}/api/products/categories/order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ order: categories })
        });
      } catch (e) {
        // silent fail; localStorage still persists locally
        console.warn('Failed to save category order', e);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [categories]);

  // Removed unused toggleCategory function

  // handleApplyFilters removed (no UI trigger currently)

  // Removed stray selectedCategory declaration and misplaced code

  const handleEdit = (product) => {
    // Always show the original price in the price field
    setForm({
      name: product.name,
      description: product.description,
      careGuide: product.careGuide || '',
      category: product.category,
      tags: Array.isArray(product.tags) && product.tags.length ? product.tags : (product.category ? [product.category] : []),
      stock: product.stock,
      openingStock: product.openingStock || 0,
      price: product.price,
      discount: product.discount,
      discountType: 'percentage', // Always percentage
      image: product.image,
      images: product.images || []  // Load existing carousel images
    });
    setSelectedProductId(product._id);
    setShowModal(true);
  };

  // Check if we need to open a specific product modal (from navigation state)
  useEffect(() => {
    const fetchAndOpenProduct = async () => {
      if (location.state?.openProductId) {
        console.log('Looking for product ID:', location.state.openProductId);
        
        // First check if product is already in loaded products
        let product = products.find(p => p._id === location.state.openProductId);
        
        // If not found in current products, fetch it directly
        if (!product) {
          console.log('Product not in current page, fetching directly...');
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_API}/api/products/${location.state.openProductId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            if (res.ok) {
              product = await res.json();
              console.log('Fetched product:', product);
            }
          } catch (err) {
            console.error('Failed to fetch product:', err);
          }
        }
        
        if (product) {
          console.log('Opening modal for product:', product.name);
          handleEdit(product);
        }
        
        // Clear the state to prevent re-opening on subsequent renders
        window.history.replaceState({}, document.title);
      }
    };
    
    fetchAndOpenProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleToggleTag = (tag) => {
    setForm(prev => {
      const has = prev.tags.includes(tag);
      const nextTags = has ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag];
      // Keep legacy category aligned to first selected tag for compatibility
      const nextCategory = nextTags.length ? nextTags[0] : '';
      return { ...prev, tags: nextTags, category: nextCategory };
    });
  };

  const handleAddImageURL = () => {
    const url = prompt('Enter image URL:');
    if (url && url.trim()) {
      const allImages = [form.image, ...(form.images || [])].filter(Boolean);
      allImages.push(url.trim());
      setForm({ 
        ...form, 
        image: allImages[0],
        images: allImages.slice(1)
      });
    }
  };

  // Removed unused convertToWebP helper to satisfy CI linting

  const handleAddImageFile = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        alert('Image is too large. Maximum allowed size is 2MB.');
        return;
      }
      try {
        // Upload image to backend (Cloudinary)
        const formData = new FormData();
        formData.append('image', file);
        const response = await fetch(`${process.env.REACT_APP_API}/api/upload/image`, {
          method: 'POST',
          body: formData
        });
        if (!response.ok) {
          throw new Error('Image upload failed');
        }
        const data = await response.json();
        const imageUrl = data.url;
        const allImages = [form.image, ...(form.images || [])].filter(Boolean);
        allImages.push(imageUrl);
        setForm({
          ...form,
          image: allImages[0],
          images: allImages.slice(1)
        });
      } catch (error) {
        console.error('Image upload error:', error);
        alert('Failed to upload image. Please try another file.');
      }
    }
    e.target.value = '';
  };

  const handleSetMainImage = (index) => {
    const allImages = [form.image, ...(form.images || [])].filter(Boolean);
    const newMainImage = allImages[index];
    allImages.splice(index, 1);
    allImages.unshift(newMainImage);
    setForm({
      ...form,
      image: allImages[0],
      images: allImages.slice(1)
    });
  };

  const handleRemoveImage = (index) => {
    const allImages = [form.image, ...(form.images || [])].filter(Boolean);
    allImages.splice(index, 1);
    setForm({
      ...form,
      image: allImages[0] || '',
      images: allImages.slice(1)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent duplicate product name (case-insensitive)
    if (!selectedProductId) {
      const duplicate = products.find(p => p.name.trim().toLowerCase() === form.name.trim().toLowerCase());
      if (duplicate) {
        alert('A product with this name already exists. Please choose a different name.');
        return;
      }
    }
    const token = localStorage.getItem('token');
    const method = selectedProductId ? 'PUT' : 'POST';
    const url = selectedProductId
      ? `${process.env.REACT_APP_API}/api/products/${selectedProductId}`
      : `${process.env.REACT_APP_API}/api/products`;
    try {
      // Always send discountType as 'percentage'
      const payload = { ...form, discountType: 'percentage' };
      // Ensure payload.tags is an array, default to [category] if empty
      if (!Array.isArray(payload.tags) || payload.tags.length === 0) {
        payload.tags = payload.category ? [payload.category] : [];
      }
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save product');
      }
      
      setShowModal(false);
      setSelectedProductId(null);
      setForm({ name: '', description: '', careGuide: '', category: '', tags: [], stock: '', openingStock: '', price: '', discount: '', discountType: 'percentage', image: '', images: [] });
      fetchProducts();
    } catch (err) {
      console.error('Failed to save product:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedProductId) return;
    setShowDeleteProductModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (!selectedProductId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API}/api/products/${selectedProductId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete product');
      }
      
      setShowModal(false);
      setSelectedProductId(null);
      setForm({ name: '', description: '', careGuide: '', category: '', tags: [], stock: '', openingStock: '', price: '', discount: '', discountType: 'percentage', image: '', images: [] });
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const confirmDeleteCategory = () => {
    if (!categoryToEdit) return;
    setCategories(prev => prev.filter(c => c !== categoryToEdit));
    if (selectedCategory === categoryToEdit) setSelectedCategory('All');
    setShowEditCategoryModal(false);
    setCategoryToEdit(null);
  };

  const handleAddStockSubmit = async () => {
    if (!addStockSelectedProduct) return alert('Please select a product to add stock to.');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/${addStockSelectedProduct._id}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ quantity: Number(addStockQuantity) })
      });
      if (!res.ok) {
        // Try JSON first, then fall back to text
        let serverMsg = '';
        try {
          const err = await res.json();
          serverMsg = err?.message || JSON.stringify(err);
        } catch (e) {
          try {
            serverMsg = await res.text();
          } catch (e2) {
            serverMsg = '';
          }
        }
        const code = res.status;
        throw new Error(serverMsg ? `Server ${code}: ${serverMsg}` : `Request failed with status ${code}`);
      }
      // Refresh products
      fetchProducts();
      setShowAddStockModal(false);
      setAddStockSelectedProduct(null);
      setAddStockQuantity(1);
    } catch (err) {
      console.error('Add stock failed:', err);
      // Show more helpful error from server or status code
      alert(err.message || 'Failed to add stock');
    }
  };

  const handleRemoveStockSubmit = async () => {
    if (!removeStockSelectedProduct) return alert('Please select a product to remove stock from.');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/products/${removeStockSelectedProduct._id}/remove-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          quantity: Number(removeStockQuantity),
          soldInStore: soldInStore,
          note: soldInStore ? 'Sold in-store' : 'Stock removed (waste/damage)'
        })
      });
      if (!res.ok) {
        let serverMsg = '';
        try {
          const err = await res.json();
          serverMsg = err?.message || JSON.stringify(err);
        } catch (e) {
          try {
            serverMsg = await res.text();
          } catch (e2) {
            serverMsg = '';
          }
        }
        const code = res.status;
        throw new Error(serverMsg ? `Server ${code}: ${serverMsg}` : `Request failed with status ${code}`);
      }
      // Refresh products
      fetchProducts();
      setShowRemoveStockModal(false);
      setRemoveStockSelectedProduct(null);
      setRemoveStockQuantity(1);
      setSoldInStore(true); // Reset to default
      // Success toast handled by realtime inventory:change listener; removed legacy alert.
    } catch (err) {
      console.error('Remove stock failed:', err);
      alert(err.message || 'Failed to remove stock');
    }
  };

  const exportToCSV = async () => {
    try {
      // Fetch all products without pagination
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'All') params.append('category', selectedCategory);
      if (minPrice) params.append('min', minPrice);
      if (maxPrice) params.append('max', maxPrice);
      if (stockStatus !== 'all') params.append('stockStatus', stockStatus);
      if (featuredFilter !== 'all') params.append('featured', featuredFilter === 'featured' ? 'true' : 'false');
      if (debouncedSearchQuery.trim()) params.append('search', debouncedSearchQuery.trim());
      
      // Remove pagination - get all products
      params.append('limit', '999999'); // Get all products
      params.append('page', '1');
      
      if (sortBy) {
        params.append('sort', sortBy);
      }
      
      const res = await fetch(`${process.env.REACT_APP_API}/api/products?${params.toString()}`);
      const data = await res.json();
      const allProducts = data.products || [];
      
      let csvContent = 'Product Name,Category,Stock,Price,Discount (%),Sale Price,Featured,Low Stock,Created Date\n';
      
      allProducts.forEach(product => {
        const name = escapeCSV(product.name || '');
        const category = escapeCSV(product.category || '');
        const stock = escapeCSV(product.stock || 0);
        const price = escapeCSV(product.price || 0);
        const discount = escapeCSV(product.discount || 0);
        const salePrice = product.discount > 0 
          ? escapeCSV((product.price - (product.price * (product.discount / 100))).toFixed(2))
          : escapeCSV(product.price || 0);
        const featured = escapeCSV(product.featured ? 'Yes' : 'No');
        const lowStock = escapeCSV(product.stock <= 10 ? 'Yes' : 'No');
        const created = escapeCSV(product.createdAt ? new Date(product.createdAt).toLocaleDateString() : '');
        
        csvContent += `${name},${category},${stock},${price},${discount},${salePrice},${featured},${lowStock},${created}\n`;
      });
      
      const filename = `products_backup_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Failed to export products. Please try again.');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedProductId(null);
    setSelectedImageIndex(null);
    setForm({ name: '', description: '', careGuide: '', category: '', tags: [], stock: '', openingStock: '', price: '', discount: '', discountType: 'percentage', image: '', images: [] });
  };

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
        {/* Empty green bar for accent */}
      </div>
      {/* Navbar below header, white background */}
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '12px' : '16px', alignItems: 'center', marginBottom: 24 }}>
          <motion.h2 
            style={{ color: '#222', fontWeight: 700, fontSize: isMobile ? '1.5rem' : '2rem', flex: isMobile ? '1 1 100%' : '1', minWidth: isMobile ? 'auto' : 200, marginBottom: isMobile ? 8 : 0 }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            Product Management
          </motion.h2>
          <motion.button
            style={{
              background: '#7a9c3a',
              color: '#fff',
              padding: isMobile ? '10px 16px' : '12px 24px',
              borderRadius: 8,
              border: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'background 0.2s',
              flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 auto',
              minWidth: isMobile ? 0 : 180,
              boxShadow: '0 2px 8px rgba(122,156,58,0.08)',
            }}
            whileHover={{ scale: 1.02, backgroundColor: '#5e7c2a' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setSelectedProductId(null);
              setForm({ name: '', description: '', careGuide: '', category: '', tags: [], stock: '', openingStock: '', price: '', discount: '', discountType: 'percentage', image: '', images: [] });
              setShowModal(true);
            }}
          >
            + ADD NEW PRODUCT
          </motion.button>
          <motion.button
            style={{
              background: '#388e3c',
              color: '#fff',
              padding: isMobile ? '10px 16px' : '12px 24px',
              borderRadius: 8,
              border: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'background 0.2s',
              flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 auto',
              minWidth: isMobile ? 0 : 140,
              boxShadow: '0 2px 8px rgba(56,140,60,0.08)'
            }}
            whileHover={{ scale: 1.02, backgroundColor: '#2e7d32' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setAddStockSelectedProduct(null);
              setAddStockQuantity(1);
              setAddStockSearch('');
              setAddStockProducts([]);
              setShowAddStockModal(true);
            }}
          >
            + ADD STOCK
          </motion.button>
          <motion.button
            style={{
              background: '#d32f2f',
              color: '#fff',
              padding: isMobile ? '10px 16px' : '12px 24px',
              borderRadius: 8,
              border: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'background 0.2s',
              flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 auto',
              minWidth: isMobile ? 0 : 160,
              boxShadow: '0 2px 8px rgba(211,47,47,0.08)'
            }}
            whileHover={{ scale: 1.02, backgroundColor: '#c62828' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setRemoveStockSelectedProduct(null);
              setRemoveStockQuantity(1);
              setRemoveStockSearch('');
              setSoldInStore(true);
              setRemoveStockProducts([]);
              setShowRemoveStockModal(true);
            }}
          >
            - REMOVE STOCK
          </motion.button>
          <motion.button
            style={{
              background: '#2e7d32',
              color: '#fff',
              padding: isMobile ? '10px 16px' : '12px 24px',
              borderRadius: 8,
              border: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer',
              transition: 'background 0.2s',
              flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 auto',
              minWidth: isMobile ? 0 : 140,
              boxShadow: '0 2px 8px rgba(46,125,50,0.08)'
            }}
            whileHover={{ scale: 1.02, backgroundColor: '#1b5e20' }}
            whileTap={{ scale: 0.98 }}
            onClick={exportToCSV}
          >
            📊 Export CSV
          </motion.button>
        </div>

        {/* Mobile Filter Toggle + Search */}
        <div className="mobile-filter-search-container" style={{ 
          display: 'none', 
          gap: '12px', 
          marginBottom: '24px',
          marginTop: '20px',
          alignItems: 'center',
          padding: '0 4px'
        }}>
          <motion.button 
            className="filter-toggle-btn"
            onClick={() => setFilterOpen(!filterOpen)}
            style={{
              background: '#7a9c3a',
              color: '#fff',
              padding: '14px 24px',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: '48px',
              minWidth: '110px',
              boxShadow: '0 2px 12px rgba(122,156,58,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            whileTap={{ scale: 0.95 }}
          >
            {filterOpen ? '✕' : '☰ Filter'}
          </motion.button>
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            style={{
              flex: 1,
              padding: '14px 16px',
              borderRadius: '10px',
              border: '2px solid #e0e0e0',
              fontSize: '1rem',
              minWidth: '200px',
              minHeight: '48px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Backdrop overlay for mobile filter */}
        <AnimatePresence>
        {filterOpen && (
          <motion.div 
            className="filter-backdrop"
            onClick={() => setFilterOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 999
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
        </AnimatePresence>

        <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: 32, flexWrap: 'wrap', width: '100%' }}>
          {/* Sidebar - Now on the right */}
          <div className={`catalog-sidebar ${filterOpen ? 'mobile-open' : ''}`}>
            {/* Filter drawer header for mobile */}
            <div className="filter-drawer-header" style={{
              display: 'none',
              padding: '20px',
              borderBottom: '2px solid #f0f0f0',
              marginBottom: '20px',
              background: '#fafafa'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: '#2c2c2c' }}>Filters</h3>
                <button 
                  onClick={() => setFilterOpen(false)}
                  style={{
                    background: '#7a9c3a',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#fff',
                    padding: '8px 12px',
                    lineHeight: 1,
                    borderRadius: '8px',
                    minWidth: '44px',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Desktop Search Bar */}
            <motion.div 
              className="desktop-search-bar" 
              style={{ width: '90%', marginBottom: '20px' }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </motion.div>

            {/* Enhanced Filters */}
            <motion.div
              style={{ width: '90%', marginBottom: 24 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <h3 style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: 12 }}>Filters</h3>
              
              {/* Price Range */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.9rem', color: '#666', marginBottom: 6 }}>Price Range</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number"
                    placeholder="Min"
                    value={minPrice}
                    onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem' }}
                  />
                  <span style={{ color: '#999' }}>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={maxPrice}
                    onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem' }}
                  />
                </div>
              </div>

              {/* Stock Status */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.9rem', color: '#666', marginBottom: 6 }}>Stock Status</label>
                <select
                  value={stockStatus}
                  onChange={(e) => { setStockStatus(e.target.value); setPage(1); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}
                >
                  <option value="all">All Stock Levels</option>
                  <option value="in-stock">In Stock (&gt;10)</option>
                  <option value="low-stock">Low Stock (≤10)</option>
                  <option value="out-of-stock">Out of Stock (0)</option>
                </select>
              </div>

              {/* Featured Filter */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.9rem', color: '#666', marginBottom: 6 }}>Featured</label>
                <select
                  value={featuredFilter}
                  onChange={(e) => { setFeaturedFilter(e.target.value); setPage(1); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}
                >
                  <option value="all">All Products</option>
                  <option value="featured">Featured Only</option>
                  <option value="non-featured">Non-Featured</option>
                </select>
              </div>

              {/* Sort By */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.9rem', color: '#666', marginBottom: 6 }}>Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.9rem', background: '#fff' }}
                >
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="price-asc">Price (Low to High)</option>
                  <option value="price-desc">Price (High to Low)</option>
                  <option value="stock-asc">Stock (Low to High)</option>
                  <option value="stock-desc">Stock (High to Low)</option>
                  <option value="createdAt-desc">Newly Added (Latest first)</option>
                  <option value="createdAt-asc">Oldest First</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              <button
                onClick={() => {
                  setMinPrice('');
                  setMaxPrice('');
                  setStockStatus('all');
                  setFeaturedFilter('all');
                  setSortBy('name-asc');
                  setSelectedCategory('All');
                  setSearchQuery('');
                  setPage(1);
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e8e8e8'}
                onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
              >
                Clear All Filters
              </button>
            </motion.div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '90%', marginBottom: 6 }}>
              <motion.h3 
                style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 12 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                Categories
              </motion.h3>
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setReorderMode(v => !v)}
                style={{
                  background: reorderMode ? '#2e7d32' : '#ffffff',
                  color: reorderMode ? '#fff' : '#2e7d32',
                  border: '1px solid #2e7d32',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >{reorderMode ? 'Done Reordering' : 'Reorder'}</motion.button>
            </div>
            <motion.div 
              style={{ width: '90%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <motion.div
                key="all"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  background: selectedCategory === 'All' ? '#e9f5e1' : 'transparent',
                  fontWeight: selectedCategory === 'All' ? 600 : 400,
                  color: selectedCategory === 'All' ? '#388e3c' : '#222',
                  borderRadius: selectedCategory === 'All' ? 8 : 0,
                  transition: 'background 0.2s',
                }}
                onClick={() => { setSelectedCategory('All'); setPage(1); }}
              >
                <span style={{ fontSize: '1rem' }}>All</span>
              </motion.div>
              {reorderMode ? (
                <>
                  <div style={{ fontSize: 12, color: '#777', margin: '8px 0 6px 2px' }}>Drag to reorder</div>
                  <Reorder.Group axis="y" values={categories} onReorder={setCategories} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {categories.map((cat, idx) => (
                      <Reorder.Item key={cat} value={cat} style={{ listStyle: 'none' }}>
                        <motion.div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'grab',
                            background: selectedCategory === cat ? '#e9f5e1' : 'transparent',
                            fontWeight: selectedCategory === cat ? 600 : 400,
                            color: selectedCategory === cat ? '#388e3c' : '#222',
                            borderRadius: selectedCategory === cat ? 8 : 0,
                            transition: 'background 0.2s',
                          }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: 0.2 + idx * 0.03 }}
                          whileTap={{ cursor: 'grabbing' }}
                          onClick={() => { /* disable category switch while reordering */ }}
                        >
                          {/* drag handle */}
                          <span title="Drag" style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8, color: '#aaa' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="5" cy="7" r="1.5"/>
                              <circle cx="5" cy="12" r="1.5"/>
                              <circle cx="5" cy="17" r="1.5"/>
                              <circle cx="12" cy="7" r="1.5"/>
                              <circle cx="12" cy="12" r="1.5"/>
                              <circle cx="12" cy="17" r="1.5"/>
                            </svg>
                          </span>
                          <span style={{ fontSize: '1rem', flex: 1 }}>{cat}</span>
                          <motion.span
                            title="Edit category"
                            style={{
                              color: '#7a9c3a',
                              fontSize: '1rem',
                              marginLeft: 4,
                              cursor: 'pointer',
                              padding: '2px 6px',
                              borderRadius: '50%',
                              transition: 'background 0.2s, color 0.2s',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            whileHover={{ scale: 1.1, backgroundColor: '#eef6e9' }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              setFilterOpen(false);
                              setCategoryToEdit(cat);
                              setEditCategoryName(cat);
                              setShowEditCategoryModal(true);
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7a9c3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                            </svg>
                          </motion.span>
                        </motion.div>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                </>
              ) : (
                <>
                  {categories.map((cat, idx) => (
                    <motion.div
                      key={cat}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        background: selectedCategory === cat ? '#e9f5e1' : 'transparent',
                        fontWeight: selectedCategory === cat ? 600 : 400,
                        color: selectedCategory === cat ? '#388e3c' : '#222',
                        borderRadius: selectedCategory === cat ? 8 : 0,
                        transition: 'background 0.2s',
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 + idx * 0.03 }}
                      onClick={() => { setSelectedCategory(cat); setPage(1); }}
                    >
                      <span style={{ fontSize: '1rem', flex: 1, cursor: 'pointer' }}>{cat}</span>
                      <motion.span
                        title="Edit category"
                        style={{
                          color: '#7a9c3a',
                          fontSize: '1rem',
                          marginLeft: 4,
                          cursor: 'pointer',
                          padding: '2px 6px',
                          borderRadius: '50%',
                          transition: 'background 0.2s, color 0.2s',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        whileHover={{ scale: 1.1, backgroundColor: '#eef6e9' }}
                        whileTap={{ scale: 0.9 }}
                        onClick={e => {
                          e.stopPropagation();
                          setFilterOpen(false);
                          setCategoryToEdit(cat);
                          setEditCategoryName(cat);
                          setShowEditCategoryModal(true);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7a9c3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </motion.span>
                    </motion.div>
                  ))}
                </>
              )}
            </motion.div>
            <button
              style={{
                marginTop: 24,
                background: '#7a9c3a',
                color: '#fff',
                padding: '10px 24px',
                borderRadius: 8,
                border: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                width: '90%',
                cursor: 'pointer',
                transition: 'background 0.2s',
                boxShadow: '0 2px 8px rgba(122,156,58,0.08)',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#5e7c2a')}
              onMouseOut={e => (e.currentTarget.style.background = '#7a9c3a')}
              onClick={() => setShowCategoryModal(true)}
            >
              + ADD NEW CATEGORY
            </button>
          </div>
          {/* Main Content */}
          <div style={{ flex: 1, background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '32px 2vw', minHeight: 600, width: '100%' }}>
            {/* Product Grid */}
            <motion.div
              className="product-grid"
              style={{
                marginBottom: 32,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {loading ? (
                // Skeleton loading cards (match page size: 9 desktop, 8 mobile)
                Array.from({ length: isMobile ? 8 : 9 }).map((_, idx) => (
                  <div 
                    key={`skeleton-${idx}`}
                    className="admin-product-card skeleton-card"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: '320px',
                      border: '2px solid #f0f0f0',
                      borderRadius: 16,
                      background: '#fff',
                      padding: '16px',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Skeleton image */}
                    <div style={{
                      width: '160px',
                      height: '160px',
                      borderRadius: '12px',
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite',
                      marginBottom: '12px',
                    }} />
                    {/* Skeleton title */}
                    <div style={{
                      width: '80%',
                      height: '20px',
                      borderRadius: '4px',
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite',
                      marginBottom: '8px',
                    }} />
                    {/* Skeleton price */}
                    <div style={{
                      width: '50%',
                      height: '16px',
                      borderRadius: '4px',
                      background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite',
                      marginBottom: '12px',
                    }} />
                    {/* Skeleton buttons */}
                    <div style={{ display: 'flex', gap: '8px', width: '100%', marginTop: 'auto' }}>
                      <div style={{
                        flex: 1,
                        height: '36px',
                        borderRadius: '8px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                      }} />
                      <div style={{
                        flex: 1,
                        height: '36px',
                        borderRadius: '8px',
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                      }} />
                    </div>
                  </div>
                ))
              ) : (
                <AnimatePresence mode="popLayout">
                  {products.map((product, idx) => {
                const displayStock = product.stock;
                return (
                  <motion.div 
                    key={product._id || idx} 
                    className="admin-product-card" 
                    style={{
                      display: 'flex',
                      position: 'relative',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: '320px',
                      boxSizing: 'border-box',
                      border: '2px solid #e5e5e5',
                      borderRadius: 16,
                      background: '#fff',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2, delay: idx * 0.03 }}
                    layout
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Featured toggle */}
                    <button
                      title={product.featured ? 'Unmark as featured' : 'Mark as featured'}
                      className={`feature-toggle ${product.featured ? 'featured' : 'not-featured'}`}
                      onClick={e => { e.stopPropagation(); e.preventDefault(); handleToggleFeatured(product._id, !!product.featured); }}
                      aria-pressed={!!product.featured}
                    >
                      <svg className="star" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 .587l3.668 7.431 8.2 1.192-5.934 5.789 1.402 8.169L12 18.896l-7.336 3.471 1.402-8.169L.132 9.21l8.2-1.192z" />
                      </svg>
                    </button>

                    {/* Low-stock badge (shows when stock <= 10 or admin flagged). Click to toggle admin lowStock flag */}
                    {( (Number(product.stock) <= 10) || product.lowStock ) && (
                      <button
                        title={ `Low stock — ${product.stock} left` }
                        className="low-stock-badge"
                        onClick={e => { e.stopPropagation(); e.preventDefault(); handleToggleLowStock(product._id, !!product.lowStock); }}
                        aria-pressed={!!product.lowStock}
                      >
                        <span className="low-stock-label">LOW</span>
                      </button>
                    )}

                    {/* Sale is derived from discount; use discount field rather than separate onSale flag */}
                    <div style={{
                      position: 'relative',
                      background: '#fff',
                      borderRadius: 16,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: 200,
                      marginBottom: 12,
                      overflow: 'hidden',
                    }}>
                      <img src={product.image || '/default-plant.png'} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16 }} />
                      {product.discount && Number(product.discount) > 0 ? (
                        <div className="sale-badge">SALE</div>
                      ) : null}
                    </div>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 2, marginTop: 2, padding: '0 16px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ textAlign: 'left', fontWeight: 600, fontSize: '1.05rem', marginBottom: 2 }}>{product.name}</div>
                        <div style={{ textAlign: 'left', color: '#666', fontSize: '0.92rem', marginBottom: 2 }}>Stock: {displayStock}</div>
                        <div style={{ textAlign: 'left', marginBottom: 2 }}>
                          {product.discount && Number(product.discount) > 0 ? (
                            <>
                              <span className="price-old">₱{product.price}</span>
                              <span className="price-new">₱{
                                  (Number(product.price) - (Number(product.price) * (Number(product.discount) / 100))).toFixed(2)
                                }</span>
                            </>
                          ) : (
                            <span style={{ color: '#388e3c', fontWeight: 600, fontSize: '1rem' }}>₱{product.price}</span>
                          )}
                        </div>
                      </div>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto', transition: 'color 0.2s' }} onMouseOver={e => (e.currentTarget.firstChild.style.fill = '#388e3c')} onMouseOut={e => (e.currentTarget.firstChild.style.fill = '#7a9c3a')} onClick={() => handleEdit(product)}>
                        <svg width="22" height="22" fill="#7a9c3a" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75l11.06-11.06-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
                </AnimatePresence>
              )}
            </motion.div>
            {/* Pagination */}
            <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1.5px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginTop: 24, marginBottom: 8 }}>
              <div style={{ color: '#6b7280', fontSize: '0.9rem', fontWeight: 500 }}>
                Showing {products.length > 0 ? ((page - 1) * (isMobile ? 8 : 12)) + 1 : 0}-{Math.min(page * (isMobile ? 8 : 12), totalCount)} of {totalCount} products
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                style={{
                  background: page === 1 ? '#f3f4f6' : '#fff',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontWeight: 600,
                  color: page === 1 ? '#9ca3af' : '#4a5568',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: 44,
                  opacity: page === 1 ? 0.4 : 1,
                }}
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                First
              </button>
              <button
                style={{
                  background: page === 1 ? '#f3f4f6' : '#fff',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontWeight: 600,
                  color: page === 1 ? '#9ca3af' : '#4a5568',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: 44,
                  opacity: page === 1 ? 0.4 : 1,
                }}
                disabled={page === 1}
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 || 
                  pageNum === totalPages || 
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      style={{
                        background: page === pageNum ? 'linear-gradient(135deg, #7a9c3a 0%, #88ab47 100%)' : '#fff',
                        border: page === pageNum ? '2px solid #7a9c3a' : '2px solid #e5e7eb',
                        borderRadius: 10,
                        padding: '10px 16px',
                        fontWeight: 600,
                        color: page === pageNum ? '#fff' : '#4a5568',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minHeight: 44,
                        minWidth: 44,
                        boxShadow: page === pageNum ? '0 2px 8px rgba(122, 156, 58, 0.2)' : 'none',
                      }}
                      onMouseOver={e => {
                        if (page !== pageNum) {
                          e.currentTarget.style.borderColor = '#7a9c3a';
                          e.currentTarget.style.background = '#f7faf4';
                        }
                      }}
                      onMouseOut={e => {
                        if (page !== pageNum) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = '#fff';
                        }
                      }}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} style={{ color: '#9ca3af', padding: '0 8px' }}>...</span>;
                }
                return null;
              })}
              <button
                style={{
                  background: page === totalPages ? '#f3f4f6' : '#fff',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontWeight: 600,
                  color: page === totalPages ? '#9ca3af' : '#4a5568',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: 44,
                  opacity: page === totalPages ? 0.4 : 1,
                }}
                disabled={page === totalPages}
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
              <button
                style={{
                  background: page === totalPages ? '#f3f4f6' : '#fff',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontWeight: 600,
                  color: page === totalPages ? '#9ca3af' : '#4a5568',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: 44,
                  opacity: page === totalPages ? 0.4 : 1,
                }}
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              >
                Last
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Green Footer Bar */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>© {new Date().getFullYear()} Chiqui's Garden</span>
      </div>
      {/* Modal for add/edit product - Redesigned */}
      <AnimatePresence>
        {showModal && (
          <motion.div 
            className="modal-overlay" 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              background: 'rgba(0,0,0,0.6)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              zIndex: 1000,
              padding: '0'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCancel();
              }
            }}
          >
            <motion.div 
              className="modal-content product-modal" 
              style={{ 
                background: '#fff', 
                width: '100%',
                maxWidth: '1000px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: '12px'
              }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Fixed Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e0e0e0',
                background: '#fff',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                    {selectedProductId ? 'Edit Product' : 'Add Product'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '4px 8px',
                      lineHeight: 1
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#333'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#666'}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Scrollable Body */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <form id="product-form" onSubmit={handleSubmit} style={{ 
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    padding: '24px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '32px'
                  }}>
                  {/* Left Column - Form Fields */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Product Name */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Product Name *
                      </label>
                      <input 
                        name="name" 
                        placeholder="Enter product name" 
                        value={form.name} 
                        onChange={handleFormChange} 
                        required 
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          transition: 'border-color 0.2s',
                          outline: 'none'
                        }} 
                        onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Description
                      </label>
                      <textarea 
                        name="description" 
                        placeholder="Enter product description" 
                        value={form.description} 
                        onChange={handleFormChange} 
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          minHeight: '80px',
                          resize: 'vertical',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          fontFamily: 'inherit'
                        }} 
                        onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                    </div>

                    {/* Care Guide */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Care Guide
                      </label>
                      <textarea 
                        name="careGuide" 
                        placeholder="Enter care instructions" 
                        value={form.careGuide} 
                        onChange={handleFormChange} 
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          minHeight: '80px',
                          resize: 'vertical',
                          transition: 'border-color 0.2s',
                          outline: 'none',
                          fontFamily: 'inherit'
                        }} 
                        onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Category
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowTagsModal(true)}
                        style={{
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          background: '#fff',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          color: '#7a9c3a',
                          transition: 'background-color 0.2s',
                          width: 'auto'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                      >
                        Edit Categories
                      </button>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                        {form.tags && form.tags.length ? (
                          <span>Selected: <strong>{form.tags.join(', ')}</strong></span>
                        ) : (
                          <span>No categories selected</span>
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Price *
                      </label>
                      <input 
                        name="price" 
                        type="number" 
                        placeholder="0.00" 
                        value={form.price} 
                        onChange={handleFormChange} 
                        required 
                        step="0.01"
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          transition: 'border-color 0.2s',
                          outline: 'none'
                        }} 
                        onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                    </div>

                    {/* Discount */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Discount (%)
                      </label>
                      <input 
                        name="discount" 
                        type="number" 
                        placeholder="0" 
                        value={form.discount} 
                        onChange={handleFormChange} 
                        min="0"
                        max="100"
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          transition: 'border-color 0.2s',
                          outline: 'none'
                        }} 
                        onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                    </div>

                    {/* Stock Quantity */}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '8px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Stock Quantity *
                      </label>
                      <input 
                        name="stock" 
                        type="number" 
                        placeholder="0" 
                        value={form.stock} 
                        onChange={handleFormChange} 
                        required 
                        readOnly={!!selectedProductId}
                        style={{ 
                          width: '100%',
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          border: '1px solid #ddd', 
                          fontSize: '1rem',
                          background: selectedProductId ? '#f5f5f5' : '#fff',
                          transition: 'border-color 0.2s',
                          outline: 'none'
                        }} 
                        onFocus={(e) => !selectedProductId && (e.currentTarget.style.borderColor = '#7a9c3a')}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                      />
                      {selectedProductId && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                          Stock is managed through the <strong>Add Stock</strong> action.
                        </div>
                      )}
                    </div>

                    {/* Super Admin - Opening Stock */}
                    {isSuperAdmin && (
                      <div style={{
                        padding: '16px',
                        background: '#fffbf0',
                        border: '2px solid #ffd700',
                        borderRadius: '8px'
                      }}>
                        <label style={{ 
                          display: 'block', 
                          fontWeight: 600, 
                          marginBottom: '8px',
                          color: '#333',
                          fontSize: '0.9rem'
                        }}>
                          Opening Stock
                        </label>
                        <input 
                          name="openingStock" 
                          type="number" 
                          placeholder="Initial stock count" 
                          value={form.openingStock} 
                          onChange={handleFormChange} 
                          style={{ 
                            width: '100%',
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            border: '1px solid #ddd', 
                            fontSize: '1rem',
                            transition: 'border-color 0.2s',
                            outline: 'none'
                          }} 
                          onFocus={(e) => e.currentTarget.style.borderColor = '#7a9c3a'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#ddd'}
                        />
                        <div style={{ 
                          fontSize: '0.85rem', 
                          color: '#856404', 
                          marginTop: '8px',
                          fontWeight: 500
                        }}>
                          <strong>⚠️ Super Admin Only:</strong> Set the initial stock count for inventory tracking and reports.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Image Upload */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontWeight: 600, 
                        marginBottom: '12px',
                        color: '#333',
                        fontSize: '0.9rem'
                      }}>
                        Product Images
                      </label>

                      {/* Upload Buttons */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                        <input 
                          id="product-images-upload" 
                          type="file" 
                          accept="image/*" 
                          style={{ display: 'none' }}
                          onChange={handleAddImageFile}
                        />
                        <label 
                          htmlFor="product-images-upload"
                          style={{ 
                            flex: 1,
                            background: '#fff', 
                            color: '#7a9c3a',
                            border: '2px solid #7a9c3a',
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            fontSize: '0.9rem', 
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            textAlign: 'center',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7a9c3a'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        >
                          <span style={{ fontSize: '1.1rem' }}>📁</span> Upload
                        </label>
                        
                        <button 
                          type="button" 
                          onClick={handleAddImageURL} 
                          style={{ 
                            flex: 1,
                            background: '#fff', 
                            color: '#7a9c3a',
                            border: '2px solid #7a9c3a',
                            padding: '12px 16px', 
                            borderRadius: '8px', 
                            fontSize: '0.9rem', 
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#7a9c3a';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = '#7a9c3a';
                          }}
                        >
                          <span style={{ fontSize: '1.1rem' }}>🔗</span> Add URL
                        </button>
                      </div>

                      {/* Images Grid */}
                      {(() => {
                        const allImages = [form.image, ...(form.images || [])].filter(Boolean);
                        return allImages.length > 0 ? (
                          <>
                            <div style={{ 
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, 1fr)',
                              gap: '12px',
                              padding: '16px',
                              background: '#fafafa',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0',
                              maxHeight: '400px',
                              overflowY: 'auto'
                            }}>
                              {allImages.map((img, idx) => (
                                <div 
                                  key={idx} 
                                  onClick={() => setSelectedImageIndex(idx)}
                                  style={{ 
                                    position: 'relative',
                                    aspectRatio: '1',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    border: selectedImageIndex === idx ? '3px solid #7a9c3a' : (idx === 0 ? '2px solid #7a9c3a' : '2px solid #ddd'),
                                    background: '#fff',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    cursor: 'pointer'
                                  }}
                                  onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <img 
                                    src={img} 
                                    alt={`Product ${idx + 1}`} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                  
                                  {/* Main Image Badge */}
                                  {idx === 0 && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '8px',
                                      left: '8px',
                                      background: '#7a9c3a',
                                      color: '#fff',
                                      padding: '4px 10px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      letterSpacing: '0.5px'
                                    }}>
                                      MAIN
                                    </div>
                                  )}

                                  {/* Selection Checkmark */}
                                  {selectedImageIndex === idx && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      background: '#7a9c3a',
                                      color: '#fff',
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '1rem',
                                      fontWeight: 'bold'
                                    }}>
                                      ✓
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {/* Action Buttons Below Grid */}
                            {selectedImageIndex !== null && (
                              <div style={{
                                display: 'flex',
                                gap: '12px',
                                marginTop: '12px'
                              }}>
                                {selectedImageIndex !== 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleSetMainImage(selectedImageIndex);
                                      setSelectedImageIndex(0);
                                    }}
                                    style={{
                                      flex: 1,
                                      background: '#7a9c3a',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '12px 16px',
                                      fontSize: '0.9rem',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#6a8c2a'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#7a9c3a'}
                                  >
                                    ⭐ Set as Main
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleRemoveImage(selectedImageIndex);
                                    setSelectedImageIndex(null);
                                  }}
                                  style={{
                                    flex: selectedImageIndex === 0 ? 1 : 'auto',
                                    background: '#dc3545',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                                >
                                  🗑️ Remove
                                </button>
                              </div>
                            )}

                            {/* Helper Text */}
                            <p style={{ 
                              fontSize: '0.8rem', 
                              color: '#666', 
                              marginTop: '12px',
                              lineHeight: 1.5
                            }}>
                              The first image is the <strong style={{ color: '#7a9c3a' }}>main image</strong>. Click an image and use "Set as Main" to change it.
                            </p>
                          </>
                        ) : (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px', 
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '2px dashed #ddd'
                          }}>
                            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.3 }}>🖼️</div>
                            <p style={{ color: '#999', fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>No images yet</p>
                            <p style={{ color: '#bbb', fontSize: '0.8rem', margin: '6px 0 0 0' }}>Upload or add URL to begin</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div style={{
                padding: '20px 24px',
                borderTop: '1px solid #e0e0e0',
                background: '#fff',
                flexShrink: 0
              }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '12px', 
                  justifyContent: 'flex-end',
                  flexWrap: 'wrap'
                }}>
                  <button 
                    type="submit"
                    form="product-form"
                    style={{ 
                        background: '#7a9c3a', 
                        color: '#fff', 
                        padding: '12px 32px', 
                        borderRadius: '8px', 
                        border: 'none', 
                        fontWeight: 600, 
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#6a8c2a'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#7a9c3a'}
                    >
                      {selectedProductId ? 'UPDATE' : 'CREATE'}
                    </button>
                    
                    {selectedProductId && (
                      <button 
                        type="button" 
                        onClick={handleDelete}
                        style={{ 
                          background: '#dc3545', 
                          color: '#fff', 
                          padding: '12px 32px', 
                          borderRadius: '8px', 
                          border: 'none', 
                          fontWeight: 600, 
                          fontSize: '1rem',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                      >
                        DELETE
                      </button>
                    )}
                    
                    <button 
                      type="button" 
                      onClick={handleCancel}
                      style={{ 
                        background: '#fff', 
                        color: '#666', 
                        padding: '12px 32px', 
                        borderRadius: '8px', 
                        border: '1px solid #ddd', 
                        fontWeight: 600, 
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modal for Add Stock (Redesigned) */}
      <AnimatePresence>
        {showAddStockModal && (
          <motion.div
            className="stock-modal-overlay"
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAddStockModal(false); }}
          >
            <motion.div
              className="stock-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="addStockTitle"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.22, ease: [0.4,0,0.2,1] }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div>
                <h2 id="addStockTitle" className="stock-modal__title">Add Stock</h2>
                <input
                  className="stock-modal__search"
                  type="text"
                  placeholder="Search products..."
                  value={addStockSearch}
                  onChange={e => setAddStockSearch(e.target.value)}
                  aria-label="Search products to add stock"
                />
                <div className="stock-modal__list" role="listbox" aria-label="Products">
                  {addStockLoading ? (
                    <div style={{ padding: '28px 12px', textAlign: 'center', color: '#607067' }}>Loading products...</div>
                  ) : addStockProducts.length === 0 && !addStockSearch.trim() ? (
                    <div style={{ padding: '34px 12px', textAlign: 'center', color: '#607067' }}>
                      <div style={{ fontSize: '2rem', marginBottom: 10 }}>🔍</div>
                      <div>Start typing to search products</div>
                    </div>
                  ) : addStockProducts.length === 0 ? (
                    <div style={{ padding: '34px 12px', textAlign: 'center', color: '#607067' }}>No products found</div>
                  ) : (
                    (addStockProducts || []).map(p => {
                      const selected = addStockSelectedProduct && addStockSelectedProduct._id === p._id;
                      return (
                        <div
                          key={p._id}
                          role="option"
                          aria-selected={selected}
                          tabIndex={0}
                          onClick={() => setAddStockSelectedProduct(p)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setAddStockSelectedProduct(p); }}
                          className={`stock-modal__item ${selected ? 'stock-modal__item--selected' : ''}`}
                        >
                          <div style={{ flex: 1 }}>
                            <div className="stock-modal__item-title">{p.name}</div>
                            <div className="stock-modal__item-sub">{p.category}</div>
                          </div>
                          <div className="stock-modal__item-stock">Stock: {p.stock}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="stock-modal__details">
                <h2 className="stock-modal__title" style={{ marginBottom: 6 }}>Quantity</h2>
                <div className="stock-modal__quantity-row">
                  <button type="button" className="stock-modal__qty-btn" aria-label="Decrease quantity" onClick={() => setAddStockQuantity(q => Math.max(1, Number(q) - 1))}>−</button>
                  <input
                    type="number"
                    className="stock-modal__qty-input"
                    value={addStockQuantity}
                    onChange={e => setAddStockQuantity(Math.max(1, Number(e.target.value || 1)))}
                    aria-label="Quantity to add"
                  />
                  <button type="button" className="stock-modal__qty-btn" aria-label="Increase quantity" onClick={() => setAddStockQuantity(q => Number(q) + 1)}>+</button>
                </div>
                <div className="stock-modal__selected-card" aria-live="polite">
                  <div style={{ fontSize: '.78rem', color: '#607067' }}>Selected Product</div>
                  <div className="stock-modal__selected-title">{addStockSelectedProduct ? addStockSelectedProduct.name : 'None'}</div>
                  <div className="stock-modal__selected-meta">
                    {addStockSelectedProduct ? `Current stock: ${addStockSelectedProduct.stock}` : 'Choose a product from the list'}
                  </div>
                </div>
                <div className="stock-modal__actions">
                  <button
                    type="button"
                    onClick={handleAddStockSubmit}
                    disabled={!addStockSelectedProduct}
                    className="stock-modal__btn-primary"
                  >Add Stock</button>
                  <button
                    type="button"
                    onClick={() => setShowAddStockModal(false)}
                    className="stock-modal__btn-secondary"
                  >Cancel</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modal for Remove Stock (Redesigned) */}
      <AnimatePresence>
        {showRemoveStockModal && (
          <motion.div
            className="stock-modal-overlay"
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowRemoveStockModal(false); }}
          >
            <motion.div
              className="stock-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="removeStockTitle"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.22, ease: [0.4,0,0.2,1] }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div>
                <h2 id="removeStockTitle" className="stock-modal__title">{soldInStore ? 'Sell In-Store' : 'Remove Stock'}</h2>
                <input
                  className="stock-modal__search"
                  type="text"
                  placeholder="Search products..."
                  value={removeStockSearch}
                  onChange={e => setRemoveStockSearch(e.target.value)}
                  aria-label="Search products to remove stock"
                />
                <div className="stock-modal__list" role="listbox" aria-label="Products">
                  {removeStockLoading ? (
                    <div style={{ padding: '28px 12px', textAlign: 'center', color: '#607067' }}>Loading products...</div>
                  ) : removeStockProducts.length === 0 && !removeStockSearch.trim() ? (
                    <div style={{ padding: '34px 12px', textAlign: 'center', color: '#607067' }}>
                      <div style={{ fontSize: '2rem', marginBottom: 10 }}>🔍</div>
                      <div>Start typing to search products</div>
                    </div>
                  ) : removeStockProducts.length === 0 ? (
                    <div style={{ padding: '34px 12px', textAlign: 'center', color: '#607067' }}>No products found</div>
                  ) : (
                    (removeStockProducts || []).map(p => {
                      const selected = removeStockSelectedProduct && removeStockSelectedProduct._id === p._id;
                      return (
                        <div
                          key={p._id}
                          role="option"
                          aria-selected={selected}
                          tabIndex={0}
                          onClick={() => setRemoveStockSelectedProduct(p)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setRemoveStockSelectedProduct(p); }}
                          className={`stock-modal__item ${selected ? 'stock-modal__item--selected' : ''}`}
                        >
                          <div style={{ flex: 1 }}>
                            <div className="stock-modal__item-title">{p.name}</div>
                            <div className="stock-modal__item-sub">{p.category}</div>
                          </div>
                          <div className="stock-modal__item-stock stock-modal__item-stock--remove">Stock: {p.stock}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="stock-modal__details">
                <div className={`remove-toggle ${soldInStore ? 'remove-toggle--sell' : 'remove-toggle--waste'}`}>
                  <label>
                    <input
                      type="checkbox"
                      checked={soldInStore}
                      onChange={e => setSoldInStore(e.target.checked)}
                      aria-label="Toggle sold in-store"
                    />
                    <span>{soldInStore ? 'Sold In-Store (Revenue)' : 'Remove Only (Waste/Damage)'}</span>
                  </label>
                  <small>{soldInStore ? 'Creates an order and counts toward revenue.' : 'Removes stock only; not added to revenue.'}</small>
                </div>
                <div className="stock-modal__quantity-row">
                  <button type="button" className="stock-modal__qty-btn" aria-label="Decrease quantity" onClick={() => setRemoveStockQuantity(q => Math.max(1, Number(q) - 1))}>−</button>
                  <input
                    type="number"
                    className="stock-modal__qty-input"
                    value={removeStockQuantity}
                    onChange={e => setRemoveStockQuantity(Math.max(1, Number(e.target.value || 1)))}
                    aria-label="Quantity to remove"
                  />
                  <button type="button" className="stock-modal__qty-btn" aria-label="Increase quantity" onClick={() => setRemoveStockQuantity(q => Number(q) + 1)}>+</button>
                </div>
                <div className="stock-modal__selected-card" aria-live="polite">
                  <div style={{ fontSize: '.78rem', color: '#607067' }}>Selected Product</div>
                  <div className="stock-modal__selected-title">{removeStockSelectedProduct ? removeStockSelectedProduct.name : 'None'}</div>
                  <div className="stock-modal__selected-meta">
                    {removeStockSelectedProduct ? `Current stock: ${removeStockSelectedProduct.stock}` : 'Choose a product from the list'}
                  </div>
                  {removeStockSelectedProduct && removeStockQuantity > removeStockSelectedProduct.stock && (
                    <div className="stock-modal__warning">Quantity exceeds available stock!</div>
                  )}
                </div>
                <div className="stock-modal__actions">
                  <button
                    type="button"
                    onClick={handleRemoveStockSubmit}
                    disabled={!removeStockSelectedProduct || removeStockQuantity > (removeStockSelectedProduct?.stock || 0)}
                    className="stock-modal__btn-primary"
                  >{soldInStore ? 'Sell In-Store' : 'Remove Stock'}</button>
                  <button
                    type="button"
                    onClick={() => { setShowRemoveStockModal(false); setSoldInStore(true); }}
                    className="stock-modal__btn-secondary"
                  >Cancel</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modal for add new category */}
      <AnimatePresence>
        {showCategoryModal && (
          <motion.div 
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCategoryModal(false);
                setNewCategory('');
              }
            }}
          >
            <motion.div 
              className="modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Add New Category</h3>
              <form onSubmit={e => {
                e.preventDefault();
                if (newCategory.trim() && !categories.includes(newCategory.trim())) {
                  setCategories([...categories, newCategory.trim()]);
                  setNewCategory('');
                  setShowCategoryModal(false);
                }
              }}>
                <input name="category" placeholder="Category Name" value={newCategory} onChange={e => setNewCategory(e.target.value)} required />
                <div className="modal-buttons">
                  <motion.button 
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Add Category
                  </motion.button>
                  <motion.button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => { setShowCategoryModal(false); setNewCategory(''); }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Modal for edit category */}
      <AnimatePresence>
        {showEditCategoryModal && (
          <motion.div 
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1500
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => { if (e.target === e.currentTarget) { setShowEditCategoryModal(false); setCategoryToEdit(null); } }}
          >
            <motion.div 
              className="modal-content"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Edit Category</h3>
              <form onSubmit={e => {
                e.preventDefault();
                const newName = editCategoryName.trim();
                if (!newName || !categoryToEdit) return;
                if (newName === categoryToEdit) { setShowEditCategoryModal(false); return; }
                const token = localStorage.getItem('token');
                fetch(`${process.env.REACT_APP_API}/api/products/category/rename`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ oldName: categoryToEdit, newName })
                }).then(async r => {
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    alert(err.message || 'Failed to rename category');
                    return;
                  }
                  setCategories(prev => prev.map(c => c === categoryToEdit ? newName : c));
                  if (selectedCategory === categoryToEdit) setSelectedCategory(newName);
                  setShowEditCategoryModal(false);
                  setCategoryToEdit(null);
                  setPage(1);
                  fetchProducts();
                }).catch(() => alert('Network error renaming category'));
              }}>
                <input name="editCategory" placeholder="Category Name" value={editCategoryName} onChange={e => setEditCategoryName(e.target.value)} required />
                <div className="modal-buttons" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>Save</motion.button>
                  <motion.button 
                    type="button" 
                    style={{ background: '#d32f2f' }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (!categoryToEdit) return;
                      setShowDeleteCategoryModal(true);
                    }}
                  >Delete</motion.button>
                  <motion.button type="button" className="cancel-btn" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => { setShowEditCategoryModal(false); setCategoryToEdit(null); }}>Cancel</motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tags selection modal */}
      <AnimatePresence>
        {showTagsModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1600,
              padding: 20
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowTagsModal(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{
                background: '#fff',
                borderRadius: 16,
                width: '100%',
                maxWidth: 560,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: '1px solid #e5e5e5'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: '18px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ margin: 0, fontSize: '1.05rem' }}>Select Categories</h4>
                <button onClick={() => setShowTagsModal(false)} style={{ border: 'none', background: 'transparent', fontSize: '1.25rem', cursor: 'pointer' }}>×</button>
              </div>
              <div style={{ padding: '16px 20px', maxHeight: '50vh', overflowY: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
                  {categories.map(cat => (
                    <label key={cat} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      borderRadius: 12,
                      border: '1px solid #d7d7d7',
                      background: form.tags && form.tags.includes(cat) ? '#eef7e9' : '#fff',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={form.tags && form.tags.includes(cat)}
                        onChange={() => handleToggleTag(cat)}
                        style={{ width: 16, height: 16, accentColor: '#7a9c3a' }}
                      />
                      <span style={{ fontSize: '0.95rem' }}>{cat}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ padding: '14px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => {
                    // Ensure category mirrors first tag for compatibility
                    setForm(prev => ({ ...prev, category: prev.tags[0] || '' }));
                    setShowTagsModal(false);
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 10,
                    border: '1px solid #222',
                    background: '#7a9c3a',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={showDeleteProductModal}
        onClose={() => setShowDeleteProductModal(false)}
        onConfirm={confirmDeleteProduct}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />

      <ConfirmModal
        isOpen={showDeleteCategoryModal}
        onClose={() => setShowDeleteCategoryModal(false)}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message={`Delete category '${categoryToEdit}'? Products will keep their category but it will be removed from this list.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />
    </div>
  );
}

export default AdminProductManager;
